import React, { useState, useEffect } from "react";
import { useInView } from "react-cool-inview";
import clsx from "clsx";
import { Work as WorkType } from "client";
import LargeWorkCard from "components/atoms/cards/LargeWork.card";
import _findIndex from "lodash/findIndex";
import { useWindowSize } from "hooks";
// import ReactScrollWheelHandler from "react-scroll-wheel-handler";

interface IWorkSliderProps {
  works: WorkType[];
}

const getMenuTranslateWidth = (slideIndex) => {
  let index = slideIndex - 1;
  let distance = 0;
  const elements = window.document.getElementsByClassName(
    "work-slider__menu-item"
  ) as HTMLCollectionOf<HTMLDivElement> | null;
  do {
    // Increase our distance counter
    distance += elements[index]?.offsetWidth;
    --index;
  } while (index >= 0);
  return distance;
};

function WorkSlider({ works }: IWorkSliderProps): JSX.Element {
  const [active, setActive] = useState(false); // is the slider active
  const [skipped, setSkipped] = useState(false); // is the slider active
  const [slideIndex, setSlideIndex] = useState(0); // current slide index
  const [pageYOffset, setPageYOffset] = useState(0); // updating pageYOffset for better scroll behavior
  const [menuTranslateWidth, setMenuTranslateWidth] = useState(0);
  const exitSlider = () => {
    setActive(false);
    setSkipped(true);
  };

  useEffect(() => {
    const onKeydown = (e) => {
      if (e.code === "Space") {
        exitSlider();
      }
    };
    //add eventlistener to window
    window.addEventListener("keydown", onKeydown);

    // remove event on unmount to prevent a memory leak
    () => window.removeEventListener("keydown", onKeydown);
  }, []);

  // scroll to updated pageYOffset
  // useEffect(() => {
  //   window.scrollTo(0, pageYOffset);
  // }, [pageYOffset]);

  // activate slider and trigger pageYOffset updates
  const {
    observe,
    inView,
    scrollDirection: { vertical, horizontal },
  } = useInView({
    threshold: 0.9,
    onEnter: ({ scrollDirection }) => {
      if (!skipped) {
        setPageYOffset(window.pageYOffset);
        setActive(true);
      }
    },
    onLeave: () => {
      // shhhhh
    },
  });

  // set slide index on click
  const setSlideIndexOnClick = (slug) => {
    let indexOfClickedItem = _findIndex(works, (w) => slug === w.slug);
    setSlideIndex(indexOfClickedItem);
  };

  useEffect(() => {
    let width = getMenuTranslateWidth(slideIndex);
    setMenuTranslateWidth(width);
  }, [slideIndex]);

  const moveSlide = (direction) => {
    if (!active) return;

    switch (direction) {
      case "left":
        // leaving up
        if (slideIndex <= 0) {
          setActive(false);
          break;
        }

        const previousSlide = slideIndex - 1;
        setSlideIndex(previousSlide);
        // setPageYOffset(pageYOffset);
        window.scrollTo(0, pageYOffset);
        break;

      case "right":
        // leaving down
        if (slideIndex >= works.length - 1) {
          setActive(false);
          break;
        }

        const nextSlide = slideIndex + 1;
        setSlideIndex(nextSlide);
        // setPageYOffset(pageYOffset);
        window.scrollTo(0, pageYOffset);
        break;

      default:
        break;
    }
  };
  const { width } = useWindowSize();
  const containerSize = width > 1280 ? 1140 : width - 10 * 20;
  const marginLeft = (width - containerSize) / 2;

  return (
    // <ReactScrollWheelHandler
    //   upHandler={() => moveSlide("left")}
    //   downHandler={() => moveSlide("right")}
    //   wheelConfig={[7, 30, 0.05, 0]}
    // >
    <section
      ref={observe}
      className={clsx("work-slider-wrapper", {
        "work-slider-wrapper--stuck": false,
      })}
    >
      <div className="work-slider h-full w-screen">
        <div className="work-slider-inner-wrapper overflow-hidden">
          <div className="work-slider__menu z-20 relative mt-16 mb-20 grid-cols-4 items-start">
            {works.map((work) => {
              const slug = work?.slug;
              return (
                <div
                  key={work.id}
                  className="work-slider__menu-item cursor-pointer"
                  onClick={() => {
                    setSlideIndexOnClick(slug);
                  }}
                >
                  {work.title()}
                </div>
              );
            })}
          </div>
          <div className="work-slider__slides">
            {works.map((work) => (
              <div
                className="work-slider__slide"
                key={work.id}
                onClick={() => setSlideIndexOnClick(work?.slug)}
              >
                <LargeWorkCard work={work} showTitle={false} />
              </div>
            ))}
          </div>
        </div>
      </div>
      <button
        onClick={() => exitSlider()}
        className={clsx(
          "fixed bottom-5 left-5 bg-blue px-10 py-3 text-white rounded",
          {
            hidden: true,
          }
        )}
      >
        Skip
      </button>
      <style jsx>{`
        .work-slider-wrapper {
          position: relative;
          background: white;
        }

        .work-slider-wrapper--stuck .work-slider {
          position: -webkit-sticky;
          position: sticky;
          top: 75px;
        }

        .work-slider {
          position: sticky;
          top: 0;
          overflow: visible;
          width: 100vw;
          max-width: 100%;
        }

        .work-slider-wrapper--stuck .work-slider-inner-wrapper {
          padding-left: calc(${marginLeft}px);
          position: fixed;
          background: white;
          top: 0;
          right: 0;
          left: 0;
          bottom: 0;
        }
        .work-slider-inner-wrapper {
          padding-left: calc(${marginLeft}px);
        }

        .work-slider__menu {
          text-align: left;
          display: flex;
          flex-shrink: 0;
          transition: transform 500ms ease-in-out;
          transform: translate(-${menuTranslateWidth}px, 0);
        }

        .work-slider__menu > .work-slider__menu-item {
          font-weight: 700;
          padding-right: 60px;
          font-family: Rubik, sans-serif;
          font-weight: 700;
          font-size: 3rem;
        }

        .work-slider__menu
          > .work-slider__menu-item:nth-of-type(${slideIndex + 1}) {
          opacity: 100%;
        }

        .work-slider__menu > .work-slider__menu-item {
          opacity: 12%;
        }

        .work-slider__slides {
          display: flex;
          transform: translate(-${(containerSize + 50) * slideIndex}px, 0);
          transition: transform 500ms ease-in-out;
        }

        .work-slider__slides > .work-slider__slide {
          flex-shrink: 0;
          width: ${containerSize}px;
          margin-right: 50px;
          position: relative;
          display: flex;
          justify-content: center;
          align-items: center;
          cursor: pointer;
        }

        @media screen and (max-width: 1400px) {
          .work-slider__menu > .work-slider__menu-item {
            margin-right: 30px;
            font-size: 2rem;
          }
        }

        @media screen and (max-width: 1160px) {
          .work-slider__slides > .work-slider__slide,
          .work-slider {
            width: ${containerSize}px;
          }
          .work-slider__menu {
            width: ${containerSize}px;
          }

          .work-slider__menu > .work-slider__menu-item {
            margin-right: 30px;
            font-size: 1.5rem;
          }
        }
      `}</style>
    </section>
    // </ReactScrollWheelHandler>
  );
}

export default WorkSlider;
